<template>
  <section class="space-y-3" aria-label="Resource usage">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Resource usage
      </h4>
      <div class="flex flex-wrap items-center gap-2">
        <span
          v-if="watchdogRestarts != null"
          class="text-xs text-gray-500 dark:text-gray-400"
          data-testid="monitoring-watchdog-restarts"
        >
          Watchdog restarts: {{ watchdogRestarts }}
        </span>
        <span
          v-if="staleFor != null"
          class="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20"
          data-testid="monitoring-stale"
        >
          No data for {{ staleFor }} min
        </span>
      </div>
    </div>

    <p v-if="status === 'paused'" :class="hintClass" data-testid="monitoring-hint">
      Metrics paused.
    </p>
    <p v-else-if="status === 'loading'" :class="hintClass" data-testid="monitoring-hint">
      Loading resource usage…
    </p>
    <p v-else-if="status === 'unavailable'" :class="hintClass" data-testid="monitoring-hint">
      Resource usage is currently unavailable.
    </p>
    <p v-else-if="!latest" :class="hintClass" data-testid="monitoring-hint">
      Waiting for first data. It appears a few minutes after the runner starts.
    </p>
    <template v-else-if="!history">
      <dl class="grid grid-cols-2 gap-x-6 gap-y-3" data-testid="monitoring-latest">
        <div v-for="metric in metrics" :key="metric.key" class="min-w-0 space-y-1">
          <dt
            class="truncate text-xs font-medium text-gray-500 dark:text-gray-400"
            :title="metric.label"
          >
            {{ metric.label }}
          </dt>
          <dd class="text-sm font-medium text-gray-900 dark:text-white">{{ metric.current }}</dd>
        </div>
      </dl>
      <p :class="hintClass" data-testid="monitoring-history-error">
        History is unavailable for this range; showing latest values only.
      </p>
    </template>
    <div v-else class="space-y-2" data-testid="monitoring-charts">
      <MetricChart
        v-for="metric in metrics"
        :key="metric.key"
        :label="metric.label"
        :current="metric.current"
        :points="metric.points"
        :axis="metric.axis"
        :range="range"
        :from="from"
        :to="to"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue';
import type { MonitoringBucket, MonitoringRange, RunnerMonitoringEntry } from '@/api/runners';
import {
  type Axis,
  buildSeries,
  byteAxis,
  countAxis,
  formatUsage,
  formatValue,
  percentAxis,
  type SeriesPoint,
  staleMinutes,
} from '@/lib/monitoring-format';
import type { RunnerMonitoringView } from '@/lib/runner-metrics';

// Loaded on demand so Chart.js stays out of the main bundle.
const MetricChart = defineAsyncComponent(() => import('@/components/MetricChart.vue'));

const props = defineProps<{
  status: RunnerMonitoringView['status'];
  entry: RunnerMonitoringEntry | null;
  range: MonitoringRange;
  from: number;
  to: number;
  bucketSeconds: number;
  now: number;
  concurrency?: number | null;
  /** Shown once the runner's watchdog was ever enabled. */
  watchdogRestarts?: number | null;
}>();

interface Metric {
  key: string;
  label: string;
  current: string;
  axis: Axis;
  points: SeriesPoint[];
}

const hintClass = 'text-xs text-gray-500 dark:text-gray-400';

const latest = computed(() => (props.status === 'ready' ? (props.entry?.latest ?? null) : null));
// A historyError means the history is unusable, even if the array is present.
const history = computed(() => (props.entry?.historyError ? null : (props.entry?.history ?? null)));

const staleFor = computed(() =>
  latest.value ? staleMinutes(latest.value.receivedAt, props.now) : null
);

function maxOf(values: (number | null | undefined)[]): number | null {
  const finite = values.filter((v): v is number => v != null && Number.isFinite(v));
  return finite.length ? Math.max(...finite) : null;
}

function series(pick: (bucket: MonitoringBucket) => number | null | undefined, axis: Axis) {
  return history.value ? buildSeries(history.value, props.bucketSeconds, pick, axis) : [];
}

const metrics = computed<Metric[]>(() => {
  const sample = latest.value;
  if (!sample) return [];
  const buckets = history.value ?? [];

  const cpuAxis = percentAxis();
  const memoryAxis = byteAxis(sample.memoryTotal ?? maxOf(buckets.map((b) => b.memoryAverage)));
  const result: Metric[] = [
    {
      key: 'cpu',
      label: 'CPU',
      current: formatValue(sample.cpu, cpuAxis),
      axis: cpuAxis,
      points: series((b) => b.cpuAverage, cpuAxis),
    },
    {
      key: 'memory',
      label: 'RAM',
      current: formatUsage(sample.memoryUsed, sample.memoryTotal, memoryAxis),
      axis: memoryAxis,
      points: series((b) => b.memoryAverage, memoryAxis),
    },
  ];

  if ((sample.swapTotal ?? 0) > 0) {
    const swapAxis = byteAxis(sample.swapTotal);
    result.push({
      key: 'swap',
      label: 'Swap',
      current: formatUsage(sample.swapUsed, sample.swapTotal, swapAxis),
      axis: swapAxis,
      points: series((b) => b.swapAverage, swapAxis),
    });
  }

  if (sample.jobsRunning != null) {
    // A peak above the concurrency is possible after the concurrency was lowered.
    const jobsAxis = countAxis(
      Math.max(
        props.concurrency ?? 0,
        sample.jobsRunning,
        maxOf(buckets.map((b) => b.jobsPeak)) ?? 0
      )
    );
    result.push({
      key: 'jobs',
      label: 'Jobs',
      current:
        props.concurrency != null
          ? `${sample.jobsRunning} / ${props.concurrency}`
          : String(sample.jobsRunning),
      axis: jobsAxis,
      points: series((b) => b.jobsAverage, jobsAxis),
    });
  }

  for (const disk of sample.disks ?? []) {
    const diskOf = (b: MonitoringBucket) => b.disks?.find((d) => d.id === disk.id);
    const axis = byteAxis(disk.total ?? maxOf(buckets.map((b) => diskOf(b)?.total)));
    result.push({
      key: `disk-${disk.id}`,
      label: `Disk ${disk.mount || disk.device || disk.id}`,
      current: formatUsage(disk.used, disk.total, axis),
      axis,
      points: series((b) => diskOf(b)?.usedAverage, axis),
    });
  }
  return result;
});
</script>
